import React, { memo, useEffect, useState } from "react";
import { Link } from "react-router-dom";

const PHRASES = [
  "Manage Customer Relationships",
  "Organize Leads Effortlessly",
  "Track Every Interaction",
  "Automate Follow-Ups",
  "Build Stronger Connections",
];

// Each field belongs to a profile group (index into PROFILE_ITEMS)
const FIELDS = [
  { label: "Name", value: "lead", group: 0 },
  { label: "Date of birth", value: "14 Mar 1990", group: 0 },
  { label: "Address", value: "221 MG Road", group: 2 },
  { label: "Country", value: "India", group: 2 },
  { label: "Phone", value: "+91 98765 43210", group: 3 },
  { label: "Employer", value: "Acme Corp", group: 1 },
  { label: "Company", value: "ABC pvt ltd", group: 2 },
  { label: "Previous address", value: "12 Orchard Rd", group: 2 },
];

const PROFILE_ITEMS = [
  ["Personal details", "♙"],
  ["Employment", "▦"],
  ["Address history", "⌂"],
  ["Contact info", "⌕"],
];

// ---- Layout constants (SVG units) ----
const PANEL = { x: 2, y: 10, w: 288, h: 270 };
const CARD = { x: 330, y: 10, w: 190, h: 190 };
const CARD_CX = CARD.x + CARD.w / 2;
const CARD_CY = CARD.y + CARD.h / 2;
const ROW_Y = (i) => 62 + i * 28; // text baseline of each row
const ITEM_TOP = (g) => 96 + g * 26; // top of each profile item

// Chip tree under the card: [centerX, y, label]
const CHIP_W = 58;
const CHIP_H = 16;
const BUS_Y = 212;
const L1_Y = 222;
const L2_Y = 252;
const L1 = [
  [CARD_CX - 66, L1_Y, "Calls"],
  [CARD_CX, L1_Y, "Emails"],
  [CARD_CX + 66, L1_Y, "Meetings"],
];
const L2 = [
  [CARD_CX - 66, L2_Y, "Follow-ups"],
  [CARD_CX + 66, L2_Y, "Notes"],
];

// Static wave lines, built once
const WAVES = Array.from({ length: 12 }, (_, i) =>
  `M -30 ${25 + i * 4} C 260 ${85 - i * 3}, 400 ${10 + i * 2}, 720 ${55 + i * 2} S 1160 ${95 - i * 2}, 1470 ${30 + i * 3}`
);

// ---- Hooks ----
const usePrefersReducedMotion = () => {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  return reduced;
};

// Typewriter state lives here so typing only re-renders the heading.
// The first phrase is rendered from the start, and a static sr-only sentence
// gives crawlers and screen readers the full, stable heading text.
const TypewriterHeading = ({ reduced }) => {
  const [index, setIndex] = useState(0);
  const [text, setText] = useState(PHRASES[0]);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (reduced) {
      setText(PHRASES[0]);
      return;
    }
    const phrase = PHRASES[index];
    let delay = deleting ? 35 : 70;

    if (!deleting && text === phrase) delay = 1600;
    if (deleting && text === "") {
      setDeleting(false);
      setIndex((i) => (i + 1) % PHRASES.length);
      return;
    }

    const t = setTimeout(() => {
      if (!deleting && text === phrase) return setDeleting(true);
      setText(phrase.slice(0, text.length + (deleting ? -1 : 1)));
    }, delay);
    return () => clearTimeout(t);
  }, [text, deleting, index, reduced]);

  return (
    <h1
      id="hero-heading"
      className="min-h-[2.5em] text-balance text-[26px] font-light leading-[1.25] tracking-tight sm:min-h-[2.35em] sm:text-[34px] lg:text-[38px] xl:text-[42px]"
    >
      <span className="sr-only">
        LMSBOTS lead management CRM: {PHRASES.join(", ")}
      </span>
      <span aria-hidden="true">{text}</span>
      <span
        aria-hidden="true"
        className="ml-1 inline-block h-[0.9em] w-[2px] animate-pulse bg-[#12dfbd] align-middle shadow-[0_0_8px_#12dfbd]"
      />
    </h1>
  );
};

// Labeled pill used in the chip tree
const Chip = ({ cx, y, label, fill }) => (
  <g className="cursor-pointer [&_rect]:transition-colors [&_rect]:duration-300 hover:[&_rect]:fill-[#12dfbd]">
    <rect x={cx - CHIP_W / 2} y={y} width={CHIP_W} height={CHIP_H} rx="8" fill={fill} />
    <text x={cx} y={y + 11} textAnchor="middle" fontSize="9" fontWeight="600" fill="#1f6f7a" className="pointer-events-none">
      {label}
    </text>
  </g>
);

// Illustration owns the row timer, so it never re-renders on typing
const CrmIllustration = memo(({ reduced }) => {
  const [autoRow, setAutoRow] = useState(0);
  const [pinned, setPinned] = useState(null); // row under mouse / focus / tap

  // Auto-cycle rows, but pause while the user is interacting
  useEffect(() => {
    if (reduced || pinned !== null) return;
    const id = setInterval(() => setAutoRow((r) => (r + 1) % FIELDS.length), 1800);
    return () => clearInterval(id);
  }, [reduced, pinned]);

  const activeRow = pinned ?? autoRow;
  const active = FIELDS[activeRow];
  const animate = !reduced;

  const fromY = ROW_Y(activeRow) - 4.5;
  const toY = ITEM_TOP(active.group) + 11;
  const linkPath = `M 276 ${fromY} C 303 ${fromY}, 303 ${toY}, ${CARD.x} ${toY}`;

  return (
    <svg
      viewBox="0 0 520 285"
      role="img"
      aria-label="Customer records matched into a unified CRM profile"
      className="block h-auto w-full select-none overflow-visible drop-shadow-2xl motion-reduce:drop-shadow-xl"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id="profileGradient" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#3199bd" />
          <stop offset="100%" stopColor="#2876a5" />
        </linearGradient>
        <linearGradient id="panelGradient" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#fff" stopOpacity=".14" />
          <stop offset="100%" stopColor="#fff" stopOpacity=".04" />
        </linearGradient>
        <radialGradient id="profileGlow" cx="50%" cy="50%" r="60%">
          <stop offset="0%" stopColor="#7bd9e9" stopOpacity=".65" />
          <stop offset="100%" stopColor="#7bd9e9" stopOpacity="0" />
        </radialGradient>
        <filter id="softGlow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="3" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Glow behind the profile card */}
      {animate && (
        <circle cx={CARD_CX} cy={CARD_CY} r="115" fill="url(#profileGlow)">
          <animate attributeName="opacity" values="0.3;0.7;0.3" dur="3.5s" repeatCount="indefinite" />
        </circle>
      )}

      {/* Customer data panel */}
      <rect {...PANEL} rx="8" fill="url(#panelGradient)" stroke="white" strokeOpacity=".3" />

      <g fontSize="8.5" fontWeight="700" fill="white" opacity=".7" letterSpacing=".6">
        <text x="16" y="36">FIELD</text>
        <text x="96" y="36">CUSTOMER DATA</text>
        <text x="212" y="36">CRM MATCH</text>
      </g>

      {FIELDS.map((f, i) => {
        const on = i === activeRow;
        const y = ROW_Y(i);
        return (
          <g
            key={f.label}
            tabIndex={0}
            role="button"
            aria-label={`${f.label}: ${f.value}`}
            aria-pressed={on}
            className="cursor-pointer outline-none [&_*]:transition-all [&_*]:duration-300"
            onMouseEnter={() => setPinned(i)}
            onMouseLeave={() => setPinned(null)}
            onFocus={() => setPinned(i)}
            onBlur={() => setPinned(null)}
            onClick={() => setPinned(i)}
          >
            {/* Row highlight (also the hit area) */}
            <rect
              x="8" y={y - 17} width="274" height="24" rx="5"
              fill={on ? "#12dfbd" : "transparent"} fillOpacity={on ? 0.2 : 0}
              stroke="#12dfbd" strokeOpacity={on ? 0.7 : 0}
            />
            <text x="16" y={y} fontSize="9.5" fill="white" opacity={on ? 0.95 : 0.7}>
              {f.label}
            </text>
            <text x="96" y={y} fontSize="10.5" fontWeight={on ? 700 : 600} fill="white" opacity={on ? 1 : 0.9}>
              {f.value}
            </text>
            <rect x="212" y={y - 13} width="64" height="17" rx="8.5" fill={on ? "#12dfbd" : "rgba(255,255,255,0.12)"} />
            <text
              x="244" y={y - 1.5} textAnchor="middle" fontSize="8.5" fontWeight="700"
              fill={on ? "#06382f" : "#e2e8f0"}
            >
              ✓ Matched
            </text>
          </g>
        );
      })}

      {/* Link from the active row to its profile group */}
      <path d={linkPath} fill="none" stroke="#12dfbd" strokeWidth="1.2" strokeOpacity=".9" strokeDasharray="3 3" />
      <circle cx={CARD.x} cy={toY} r="3" fill="#12dfbd" filter="url(#softGlow)" />
      {animate && (
        <circle r="2.5" fill="#12dfbd" filter="url(#softGlow)">
          <animateMotion key={activeRow} dur="1.2s" repeatCount="indefinite" path={linkPath} />
        </circle>
      )}

      {/* Profile card */}
      <g filter="url(#softGlow)">
        <rect
          {...CARD} rx="8" fill="url(#profileGradient)" stroke="#7bd9e9" strokeOpacity=".9" strokeWidth="1.2"
        />
      </g>
      <text x="346" y="32" fontSize="8.5" fill="white" fontWeight="700" letterSpacing=".8" opacity=".85">
        CUSTOMER PROFILE
      </text>
      <circle cx="361" cy="58" r="14" fill="#d9eafa" />
      <text x="361" y="62" textAnchor="middle" fontSize="11" fontWeight="700" fill="#277ca1">L</text>
      <text x="382" y="56" fontSize="12" fontWeight="700" fill="white">Lead</text>
      <text x="382" y="70" fontSize="8.5" fill="#b8f5f0">Matched · {active.label}</text>
      <line x1="342" y1="84" x2="508" y2="84" stroke="white" strokeOpacity=".25" />

      {PROFILE_ITEMS.map(([label, symbol], g) => {
        const top = ITEM_TOP(g);
        const on = g === active.group;
        return (
          <g key={label} className="[&_*]:transition-all [&_*]:duration-300">
            <rect
              x="338" y={top} width="174" height="22" rx="5"
              fill="white" fillOpacity={on ? 0.22 : 0}
            />
            <circle cx="350" cy={top + 11} r={on ? 3.5 : 2.5} fill={on ? "#12dfbd" : "#b8f5f0"} />
            <text x="361" y={top + 14.5} fontSize="10" fontWeight={on ? 700 : 400} fill="white">
              {label}
            </text>
            <rect x="488" y={top + 4} width="18" height="14" rx="3" fill={on ? "#e8fff9" : "#d9eafa"} />
            <text x="497" y={top + 14.5} textAnchor="middle" fontSize="9" fill="#277ca1" fontWeight="700">
              {symbol}
            </text>
          </g>
        );
      })}

      {/* Connector lines: card -> bus -> level 1 -> level 2 */}
      <path
        d={[
          `M ${CARD_CX} ${CARD.y + CARD.h} V ${BUS_Y}`,
          `M ${L1[0][0]} ${BUS_Y} H ${L1[2][0]}`,
          ...L1.map(([cx]) => `M ${cx} ${BUS_Y} V ${L1_Y}`),
          ...L2.map(([cx]) => `M ${cx} ${L1_Y + CHIP_H} V ${L2_Y}`),
        ].join(" ")}
        stroke="#a8f4e6" strokeOpacity=".75" strokeWidth="1" fill="none" strokeLinecap="round"
      />

      {/* Chips */}
      {L1.map(([cx, y, label]) => (
        <Chip key={label} cx={cx} y={y} label={label} fill="#e4f8f5" />
      ))}
      {L2.map(([cx, y, label]) => (
        <Chip key={label} cx={cx} y={y} label={label} fill="#c9eee8" />
      ))}
    </svg>
  );
});

// Rendered inside <main> by HomePage, so this is a plain section (no <main>
// and no forced min-h-screen wrapper that would leave a white gap below).
const HeroSection = () => {
  const reduced = usePrefersReducedMotion();

  return (
    <section
      id="hero"
      aria-labelledby="hero-heading"
      className="relative isolate min-h-[560px] w-full overflow-hidden bg-gradient-to-r from-[#28194f] via-[#48247f] to-[#702cb0] font-sans text-white sm:min-h-[620px] lg:min-h-[680px]"
    >
      {/* Ambient glow blobs */}
      <div className="pointer-events-none absolute -right-20 -top-24 h-96 w-96 rounded-full bg-fuchsia-400/15 blur-3xl" />
      <div className="pointer-events-none absolute -left-32 bottom-0 h-80 w-80 rounded-full bg-indigo-400/15 blur-3xl" />

      <div className="relative z-10 mx-auto grid min-h-[560px] w-full max-w-[1500px] items-center gap-8 px-4 pb-20 pt-24 sm:min-h-[620px] sm:gap-12 sm:px-10 sm:pb-24 sm:pt-28 lg:min-h-[680px] lg:grid-cols-[1fr_1.05fr] lg:gap-8 lg:px-14 lg:pt-32 xl:px-20">
        {/* Left content */}
        <div className="relative z-10 mx-auto max-w-xl text-center lg:mx-0 lg:text-left">
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1 text-xs font-medium text-[#12dfbd] backdrop-blur-md">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#12dfbd]" />
            Next-Gen CRM Platform
          </div>

          <TypewriterHeading reduced={reduced} />

          <p className="mx-auto mt-4 max-w-[520px] text-sm font-light leading-[1.75] text-white/90 sm:text-base lg:mx-0 lg:text-[17px]">
            Customer Relationship Management (CRM) helps businesses manage
            customer information, organize leads, track interactions, automate
            follow-ups, and build stronger customer relationships—all from one
            centralized platform.
          </p>

          <div className="mt-8 flex flex-col items-stretch gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-center lg:justify-start lg:gap-4">
            <Link
              to="/contact"
              className="inline-flex min-h-[44px] items-center justify-center rounded-lg bg-[#12dfbd] px-6 py-3 text-xs font-semibold text-[#102b37] shadow-lg shadow-[#12dfbd]/20 transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#40f0d3] hover:shadow-[#12dfbd]/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white sm:text-sm"
            >
              Book A Demo
            </Link>
            <Link
              to="/features"
              className="inline-flex min-h-[44px] items-center justify-center rounded-lg bg-white/10 px-6 py-3 text-xs font-medium text-white backdrop-blur-md transition-all duration-300 hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white sm:text-sm"
            >
              Explore Features
            </Link>
          </div>
        </div>

        {/* Right illustration */}
        <div className="relative mx-auto w-full max-w-[600px] lg:max-w-none">
          <CrmIllustration reduced={reduced} />
        </div>
      </div>

      {/* Bottom wave lines */}
      <svg
        aria-hidden="true"
        viewBox="0 0 1440 105"
        preserveAspectRatio="none"
        className="pointer-events-none absolute bottom-0 left-0 z-0 h-[65px] w-full sm:h-[85px] lg:h-[100px]"
      >
        {WAVES.map((d, i) => (
          <path key={i} d={d} fill="none" stroke="#c9b5ff" strokeWidth=".7" strokeOpacity=".35" />
        ))}
      </svg>
    </section>
  );
};

export default HeroSection;